import React, { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Tooltip
} from '@mui/material';
import ContentPasteGoIcon from '@mui/icons-material/ContentPasteGo';
import { findOverdueInvoices, formatOverdueInvoices } from '../../utils/overdueInvoices';

interface OverdueInvoicesButtonProps {
  /** Every invoice, not the filtered view — overdue work should never be hidden */
  invoices: any[];
  /** The selected broker, or 'all'. The only filter this button follows. */
  broker: string;
  onNotify: (message: string, severity: 'success' | 'error' | 'info') => void;
}

const OverdueInvoicesButton: React.FC<OverdueInvoicesButtonProps> = ({
  invoices,
  broker,
  onNotify
}) => {
  const [fallbackText, setFallbackText] = useState<string | null>(null);

  const brokerLabel = broker && broker !== 'all' ? broker : undefined;

  // Follows the broker filter (and only that), so switching brokers moves the
  // badge count with it. Every other filter is deliberately ignored.
  const overdue = useMemo(() => {
    const scoped = brokerLabel
      ? invoices.filter(invoice => invoice.broker === brokerLabel)
      : invoices;
    return findOverdueInvoices(scoped);
  }, [invoices, brokerLabel]);

  const handleClick = async () => {
    const text = formatOverdueInvoices(overdue, Date.now(), brokerLabel);

    try {
      await navigator.clipboard.writeText(text);
      onNotify(
        `Copied ${overdue.length} overdue ${overdue.length === 1 ? 'load' : 'loads'}${
          brokerLabel ? ` for ${brokerLabel}` : ''
        } to your clipboard.`,
        'success'
      );
    } catch (error) {
      // Clipboard access can be blocked; hand the text over to copy manually
      // rather than leaving the click with nothing to show for it.
      console.error('Could not write to the clipboard:', error);
      setFallbackText(text);
    }
  };

  const scopeNote = brokerLabel
    ? `Limited to ${brokerLabel}; the other filters below are ignored.`
    : 'Covers every broker; the filters below are ignored.';

  const label =
    overdue.length === 0
      ? `No ${brokerLabel ? `${brokerLabel} ` : ''}invoices are past their due date`
      : `Copy ${overdue.length} overdue ${
          overdue.length === 1 ? 'load' : 'loads'
        } — invoice #, load #, PO #, destination, invoice date and due date, most overdue first. ${scopeNote} Invoices marked PAID are excluded.`;

  return (
    <>
      <Tooltip title={label}>
        <span>
          <Badge badgeContent={overdue.length} color="error" overlap="rectangular">
            <Button
              variant="outlined"
              startIcon={<ContentPasteGoIcon />}
              onClick={handleClick}
              disabled={overdue.length === 0}
            >
              Get Overdue Invoices
            </Button>
          </Badge>
        </span>
      </Tooltip>

      <Dialog
        open={Boolean(fallbackText)}
        onClose={() => setFallbackText(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Copy overdue invoices</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Your browser blocked clipboard access. Select the text below and copy it.
          </DialogContentText>
          <TextField
            multiline
            fullWidth
            minRows={8}
            maxRows={18}
            value={fallbackText || ''}
            InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 13 } }}
            onFocus={(e) => e.target.select()}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFallbackText(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OverdueInvoicesButton;
